import {
    CheckOutlined,
    Clear,
    ModeEditOutlineOutlined,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Radio,
    Skeleton,
    TextField,
    Typography,
    alpha,
} from '@mui/material';
import {
    MouseEventHandler,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getCatalogItems } from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import {
    getSelectedOrder,
    getSelectedPoNumberIsLoading,
} from 'entities/Order/model/selectors/ordersSelectors';
import { getSelectedProperty } from 'entities/Property/model/selectors/PropertySelectors';
import { UserRole, getUserRole } from 'entities/User';
import { getNavigationBlockDialogIsShown } from 'features/NavigationBlockModal/model/selectors/navigationBlockSelectors';
import { navigationBlockModalActions } from 'features/NavigationBlockModal/model/slices/NavigationBlockModalSlice';
import {
    getPoEditState,
    getProposalFilter,
    getProposalFilterSellersDataBySeller,
    hasUnsavedPoChanges as hasUnsavedPoChangesSelector,
} from 'features/OrderProposalFilter/model/selectors/orderProposalFilteSelectors';
import { orderProposalFilterActions } from 'features/OrderProposalFilter/model/slices/OrderProposalFilterSlice';
import {
    SellerData,
    SellerDataRecord,
} from 'features/OrderProposalFilter/model/types/OrderProposalFilterSchema';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useUnmountEffect } from 'shared/lib/hooks/useUnmountEffect';
import { OrderPriceDisplay } from 'shared/ui/OrderPriceDisplay/OrderPriceDisplay';

import { editPurchaseOrderNumber } from '../../model/services/editPurchaseOrderNumber';

export const OrderProposalFilter = memo(() => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const currentUserRole = useSelector(getUserRole);
    const currentProperty = useSelector(getSelectedProperty);
    const currentOrder = useSelector(getSelectedOrder);
    const isLoadingPoNumber = useSelector(getSelectedPoNumberIsLoading);
    const currentFilter = useSelector(getProposalFilter);
    const catalogItems = useSelector(getCatalogItems);
    const sellersInProposalsBySeller = useSelector(
        getProposalFilterSellersDataBySeller,
    );
    const poEditState = useSelector(getPoEditState);
    const hasUnsavedPoChanges = useSelector(hasUnsavedPoChangesSelector);
    const dialogIsShown = useSelector(getNavigationBlockDialogIsShown);

    const poFieldError = poEditState?.hasError || false;
    const poFieldInputRef = useRef<HTMLInputElement>(null);
    const editingSellerId = poEditState?.editingSellerId || '';
    const tempPoValue = poEditState?.tempPoValue || '';

    const isSupervisor = currentUserRole === UserRole.MAINTENANCE_SUPERVISOR;

    const allowedStatuses = ['DRAFT', 'IN_REVIEW'];
    const isAllowedToEditPO: boolean =
        (allowedStatuses
            .concat(['PENDING_APPROVAL'])
            .includes(currentOrder.status) &&
            !isSupervisor) ||
        (allowedStatuses.includes(currentOrder.status) && isSupervisor);

    const items = catalogItems as CatalogItem[];

    const styleForButtons = {
        '& span': {
            margin: 0,
        },
        padding: '5px',
        minWidth: 'auto',
    };

    const isRequiredPoInProperty = useMemo(
        () => !!currentProperty?.isPoNumbersRequired,
        [currentProperty],
    );

    const sellersWithPoNumbers: SellerDataRecord = useMemo(() => {
        const poNumbers = currentOrder.poNumbers;

        const updatedSellersData: SellerDataRecord = {};

        Object.entries(sellersInProposalsBySeller).forEach(
            ([id, sellerData]) => {
                const newSellerData = { ...sellerData };
                const matchingPo = poNumbers.find(
                    (el) => el.id === sellerData.sellerUid,
                );

                if (matchingPo) {
                    newSellerData.poNumber = matchingPo.poNumber;
                } else {
                    newSellerData.poNumber = null;
                }

                updatedSellersData[id] = newSellerData;
            },
        );

        return updatedSellersData;
    }, [sellersInProposalsBySeller, currentOrder.poNumbers]);

    const isEmptyProposalsList = useMemo(() => {
        const sellersArray = Object.entries(sellersWithPoNumbers);
        return sellersArray?.length === 0;
    }, [sellersWithPoNumbers]);

    const handleApplyPoNumber = useCallback(
        async (supplierId: string) => {
            if (!poEditState) return;

            await dispatch(
                editPurchaseOrderNumber({
                    orderId: currentOrder.id,
                    supplierId,
                    poNumber: poEditState.tempPoValue,
                }),
            );

            dispatch(navigationBlockModalActions.setNavigationBlocked(false));
            dispatch(orderProposalFilterActions.setPoEditState(undefined));
        },
        [currentOrder, dispatch, poEditState],
    );

    const poOnClick = useCallback(
        (sellerData: SellerData) => {
            if (!isAllowedToEditPO) return;

            // If there are unsaved changes in another PO field, show error state
            if (
                hasUnsavedPoChanges &&
                editingSellerId !== sellerData.sellerUid
            ) {
                dispatch(orderProposalFilterActions.setPoFieldError(true));
                return;
            }

            const initialPoValue = sellerData.poNumber || '';
            dispatch(
                orderProposalFilterActions.setPoEditState({
                    editingSellerId: sellerData.sellerUid,
                    tempPoValue: initialPoValue,
                    originalPoValue: initialPoValue,
                    hasError: false,
                }),
            );
        },
        [isAllowedToEditPO, hasUnsavedPoChanges, editingSellerId, dispatch],
    );

    const onChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        selectedSellerUid: string,
    ) => {
        e.stopPropagation();
        e.preventDefault();

        dispatch(orderProposalFilterActions.setFilter(selectedSellerUid));

        const filteredCatalogItems = items.filter((i) => {
            return i.selectedProposals?.some(
                (p) => p.sellerUid === selectedSellerUid,
            );
        });

        dispatch(
            orderProposalFilterActions.setFilteredItems(filteredCatalogItems),
        );
    };

    const preventPropagation: MouseEventHandler<HTMLButtonElement> = (e) => {
        e.stopPropagation();
    };

    // Block navigation if there are unsaved changes in PO field
    useEffect(() => {
        if (hasUnsavedPoChanges) {
            dispatch(
                navigationBlockModalActions.setNavigationBlocked({
                    blocked: true,
                    allowedRoutes: [
                        // Allow navigation to item detail since PO edit sidebar is still visible (regex source string)
                        String.raw`^/orders/[^/]+/item/[^/]+$`,
                    ],
                }),
            );
        } else {
            dispatch(navigationBlockModalActions.setNavigationBlocked(false));
        }
    }, [hasUnsavedPoChanges, dispatch]);

    useUnmountEffect(() => {
        dispatch(orderProposalFilterActions.setFilter('All'));
        dispatch(navigationBlockModalActions.setNavigationBlocked(false));
    });

    const closeEditPoMode = () => {
        dispatch(navigationBlockModalActions.setNavigationBlocked(false));
        dispatch(orderProposalFilterActions.setPoEditState(undefined));
    };

    const focusPoField = useCallback(() => {
        if (poFieldInputRef.current && editingSellerId) {
            poFieldInputRef.current.focus();
            poFieldInputRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [editingSellerId]);

    // Auto focus on the PO field if there is an error
    useEffect(() => {
        if (poFieldError) {
            focusPoField();
        }
    }, [poFieldError, focusPoField]);

    // Auto focus on the PO field if the blocking dialog is closed
    const prevDialogShown = useRef(dialogIsShown);
    useEffect(() => {
        if (prevDialogShown.current && !dialogIsShown) {
            const timeoutId = setTimeout(focusPoField, 100);
            prevDialogShown.current = dialogIsShown;
            return () => clearTimeout(timeoutId);
        } else {
            prevDialogShown.current = dialogIsShown;
        }
    }, [dialogIsShown, focusPoField]);

    return (
        !isEmptyProposalsList && (
            <Box>
                <Box display="flex" alignItems="center">
                    <Radio
                        checked={currentFilter === 'All'}
                        onChange={(e) => onChange(e, 'All')}
                        onClick={preventPropagation}
                        value={'All'}
                    />
                    <Typography
                        variant="subtitle2"
                        sx={(theme) => ({
                            color: alpha(theme.palette.common?.black, 0.5),
                        })}
                    >
                        {t('Select All')}
                    </Typography>
                </Box>
                <Box gap="8px" display="flex" flexDirection="column">
                    {Object.entries(sellersWithPoNumbers)?.map(
                        ([id, sellerData]) => {
                            return (
                                sellerData.name !== 'null' && (
                                    <Box
                                        data-testclass="order-summary-seller"
                                        display="flex"
                                        flexDirection="column"
                                        width="100%"
                                        gap="2px"
                                        sx={{
                                            bgcolor: 'grey.500',
                                            borderRadius: '4px',
                                            padding: '9px',
                                        }}
                                        key={id}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            width="100%"
                                        >
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                width="100%"
                                            >
                                                <Radio
                                                    sx={{ padding: 0 }}
                                                    checked={
                                                        currentFilter === id
                                                    }
                                                    onChange={(e) =>
                                                        onChange(e, id)
                                                    }
                                                    onClick={preventPropagation}
                                                    value={id}
                                                />
                                                <Typography
                                                    data-testclass="order-summary-seller-name"
                                                    variant="subtitle2"
                                                    sx={(theme) => ({
                                                        marginLeft: '9px',
                                                        marginRight: '4px',
                                                        color: alpha(
                                                            theme.palette.common
                                                                ?.black,
                                                            0.85,
                                                        ),
                                                    })}
                                                >
                                                    {sellerData.name}
                                                </Typography>
                                                <Typography
                                                    mr="4px"
                                                    data-testclass="order-summary-seller-qty"
                                                >
                                                    (
                                                    {
                                                        sellerData.itemProposals
                                                            .length
                                                    }
                                                    )
                                                </Typography>
                                            </Box>
                                            <OrderPriceDisplay
                                                width="60px"
                                                containerHeight="24px"
                                                containerProps={{ mr: '5px' }}
                                                testClass="order-summary-seller-price"
                                                price={sellerData.totalPrice}
                                                order={currentOrder}
                                                orderHasLoadingPrices={
                                                    !sellerData.isCheckFinished
                                                }
                                            />
                                        </Box>
                                        <Box
                                            data-testclass="order-summary-seller-po"
                                            display="flex"
                                            justifyContent="space-between"
                                            width="100%"
                                            style={{ alignItems: 'center' }}
                                        >
                                            <Box style={{ flex: 1 }}>
                                                <Box
                                                    display="flex"
                                                    style={{ width: '100%' }}
                                                    sx={{ paddingLeft: '33px' }}
                                                    alignItems="center"
                                                >
                                                    <Typography
                                                        variant="body1"
                                                        title="Purchase Order"
                                                        display="flex"
                                                    >
                                                        {t('PO') + ':'}
                                                        {isRequiredPoInProperty && (
                                                            <Typography
                                                                component="span"
                                                                style={{
                                                                    color: '#BF1B28',
                                                                    lineHeight: 1,
                                                                }}
                                                            >{`*`}</Typography>
                                                        )}
                                                    </Typography>
                                                    {editingSellerId &&
                                                    editingSellerId ===
                                                        sellerData.sellerUid ? (
                                                        <TextField
                                                            inputRef={
                                                                poFieldInputRef
                                                            }
                                                            sx={{
                                                                paddingLeft:
                                                                    '3px',
                                                            }}
                                                            style={{
                                                                width: '100%',
                                                                maxWidth:
                                                                    '250px',
                                                            }}
                                                            autoFocus
                                                            value={tempPoValue}
                                                            error={poFieldError}
                                                            onKeyDown={(e) => {
                                                                if (
                                                                    e.key ===
                                                                    'Enter'
                                                                ) {
                                                                    handleApplyPoNumber(
                                                                        sellerData.sellerUid,
                                                                    );
                                                                }

                                                                if (
                                                                    e.key ===
                                                                    'Escape'
                                                                ) {
                                                                    closeEditPoMode();
                                                                }
                                                            }}
                                                            onChange={(e) => {
                                                                if (
                                                                    e.target
                                                                        .value
                                                                        .length ===
                                                                    17
                                                                )
                                                                    return;
                                                                const newValue =
                                                                    e.target
                                                                        .value;
                                                                dispatch(
                                                                    orderProposalFilterActions.setPoTempValue(
                                                                        {
                                                                            sellerId:
                                                                                sellerData.sellerUid,
                                                                            tempValue:
                                                                                newValue,
                                                                        },
                                                                    ),
                                                                );
                                                            }}
                                                            InputProps={{
                                                                sx: {
                                                                    fontSize:
                                                                        '1.4rem',
                                                                    height: '22px',
                                                                    background:
                                                                        '#fff',
                                                                    borderRadius:
                                                                        '4px',
                                                                },
                                                            }}
                                                        />
                                                    ) : (
                                                        <>
                                                            {!isLoadingPoNumber ? (
                                                                <>
                                                                    {sellerData.poNumber ? (
                                                                        <Typography
                                                                            variant="body1"
                                                                            paddingLeft="3px"
                                                                            color="#000"
                                                                            sx={{
                                                                                cursor: 'pointer',
                                                                            }}
                                                                            onClick={() => {
                                                                                poOnClick(
                                                                                    sellerData,
                                                                                );
                                                                            }}
                                                                        >
                                                                            {
                                                                                sellerData.poNumber
                                                                            }
                                                                        </Typography>
                                                                    ) : (
                                                                        <Typography
                                                                            variant="body1"
                                                                            paddingLeft="3px"
                                                                            color="#0000004D"
                                                                            sx={{
                                                                                cursor: 'pointer',
                                                                            }}
                                                                            onClick={() => {
                                                                                poOnClick(
                                                                                    sellerData,
                                                                                );
                                                                            }}
                                                                        >
                                                                            {isRequiredPoInProperty
                                                                                ? t(
                                                                                      'Required',
                                                                                  )
                                                                                : t(
                                                                                      'Optional',
                                                                                  )}
                                                                        </Typography>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <Skeleton
                                                                    sx={{
                                                                        marginLeft:
                                                                            '10px',
                                                                    }}
                                                                    width="200px"
                                                                    height="30px"
                                                                />
                                                            )}
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>
                                            {editingSellerId &&
                                            editingSellerId ===
                                                sellerData.sellerUid ? (
                                                <Box display="flex" gap="4px">
                                                    <Button
                                                        sx={styleForButtons}
                                                        startIcon={
                                                            <Clear
                                                                sx={{
                                                                    fontSize:
                                                                        '2rem !important',
                                                                }}
                                                            />
                                                        }
                                                        onClick={
                                                            closeEditPoMode
                                                        }
                                                        data-testid="btn-cancel-edit-po"
                                                    />
                                                    <Button
                                                        sx={styleForButtons}
                                                        startIcon={
                                                            <CheckOutlined
                                                                sx={{
                                                                    fontSize:
                                                                        '2rem !important',
                                                                }}
                                                            />
                                                        }
                                                        data-testid="btn-save-po"
                                                        onClick={() => {
                                                            handleApplyPoNumber(
                                                                sellerData.sellerUid,
                                                            );
                                                        }}
                                                    />
                                                </Box>
                                            ) : (
                                                isAllowedToEditPO && (
                                                    <Button
                                                        sx={styleForButtons}
                                                        data-testid="btn-edit-po"
                                                        startIcon={
                                                            <ModeEditOutlineOutlined
                                                                sx={{
                                                                    fontSize:
                                                                        '2rem !important',
                                                                }}
                                                            />
                                                        }
                                                        onClick={() => {
                                                            poOnClick(
                                                                sellerData,
                                                            );
                                                        }}
                                                    />
                                                )
                                            )}
                                        </Box>
                                    </Box>
                                )
                            );
                        },
                    )}
                </Box>
            </Box>
        )
    );
});
